/**
 * Google Drive Service
 *
 * Handles OAuth and Google Drive API operations for file storage.
 * Pro users only - allows storing files in their own Google Drive.
 */

import type {
  StorageProvider,
  StorageUploadResult,
  UploadContext,
  GoogleDriveSettings,
  GoogleDriveFileMetadata,
  FolderPermissionsResult,
  PreSharedDriveStatus,
  PreSharedDriveRole,
} from '../types/storage';
import {
  buildFolderPath,
  getAllParentPaths,
  generateUniqueFileName,
} from './storage/folderStructure';

// Google Drive API constants
const GOOGLE_DRIVE_API_BASE = 'https://www.googleapis.com/drive/v3';
const GOOGLE_DRIVE_UPLOAD_API = 'https://www.googleapis.com/upload/drive/v3';

/** Per-request timeout for Google Drive API calls (10 seconds) */
const DRIVE_API_TIMEOUT = 10_000;
/** Longer timeout for file uploads (60 seconds) */
const DRIVE_UPLOAD_TIMEOUT = 60_000;
/** Shorter timeout for lightweight validation calls */
const DRIVE_VALIDATION_TIMEOUT = 5_000;

// OAuth scope for Google Drive - drive.file gives access only to files created by this app
// Note: This is defined here for documentation; the actual scope is used in the OAuth redirect URL
const _GOOGLE_OAUTH_SCOPES = ['https://www.googleapis.com/auth/drive.file'];
void _GOOGLE_OAUTH_SCOPES; // Prevent unused variable warning

// Storage keys for localStorage
const STORAGE_KEYS = {
  ACCESS_TOKEN: 'lucidDock_googleDrive_accessToken',
  REFRESH_TOKEN: 'lucidDock_googleDrive_refreshToken',
  TOKEN_EXPIRY: 'lucidDock_googleDrive_tokenExpiry',
  USER_EMAIL: 'lucidDock_googleDrive_userEmail',
  ROOT_FOLDER_ID: 'lucidDock_googleDrive_rootFolderId',
  CONFIG_FOLDER_ID: 'lucidDock_googleDrive_configFolderId', // .luciddock folder
  FOLDER_CACHE: 'lucidDock_googleDrive_folderCache',
  CONNECTED_AT: 'lucidDock_googleDrive_connectedAt',
};


// MIME types
const MIME_TYPES = {
  FOLDER: 'application/vnd.google-apps.folder',
};

export interface DriveFile {
  id: string;
  name: string;
  mimeType: string;
  size?: string;
  webViewLink?: string;
  webContentLink?: string;
  thumbnailLink?: string;
  appProperties?: Record<string, string>;
}

export interface DriveFolder {
  id: string;
  name: string;
}

class GoogleDriveService {
  private folderCache: Map<string, string> = new Map();
  /** Serializes concurrent ensureFolderPath calls for the same path */
  private folderLocks: Map<string, Promise<string | null>> = new Map();
  /** Whether the folder cache has been validated against Drive this session */
  private cacheValidated = false;

  constructor() {
    this.loadFolderCache();
  }

  /**
   * Fetch wrapper with per-request timeout and automatic 401/403 detection.
   * Prevents any single API call from hanging indefinitely.
   */
  private async driveFetch(url: string, init: RequestInit = {}, timeoutMs = DRIVE_API_TIMEOUT): Promise<Response> {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), timeoutMs);

    try {
      const response = await fetch(url, { ...init, signal: controller.signal });

      // Detect expired tokens immediately
      if (response.status === 401 || response.status === 403) {
        this.dispatchAuthError('Google Drive session expired. Please reconnect.');
      }

      return response;
    } finally {
      clearTimeout(timer);
    }
  }

  /**
   * Load folder cache from localStorage
   */
  private loadFolderCache(): void {
    try {
      const cached = localStorage.getItem(STORAGE_KEYS.FOLDER_CACHE);
      if (cached) {
        const parsed = JSON.parse(cached);
        this.folderCache = new Map(Object.entries(parsed));
      }
    } catch (error) {
      console.error('Failed to load folder cache:', error);
    }
  }

  /**
   * Save folder cache to localStorage
   */
  private saveFolderCache(): void {
    try {
      const obj = Object.fromEntries(this.folderCache);
      localStorage.setItem(STORAGE_KEYS.FOLDER_CACHE, JSON.stringify(obj));
    } catch (error) {
      console.error('Failed to save folder cache:', error);
    }
  }

  /**
   * Validate that the cached LucidDock root folder still exists in Drive.
   * Called once per session before trusting the folder cache.
   */
  private async validateRootFolder(): Promise<boolean> {
    const rootId = this.folderCache.get('LucidDock')
      || localStorage.getItem(STORAGE_KEYS.ROOT_FOLDER_ID);
    if (!rootId) return false;

    const accessToken = await this.getValidAccessToken();
    if (!accessToken) return false;

    try {
      const response = await this.driveFetch(
        `${GOOGLE_DRIVE_API_BASE}/files/${rootId}?fields=id,trashed`,
        { headers: { Authorization: `Bearer ${accessToken}` } },
        DRIVE_VALIDATION_TIMEOUT
      );
      if (!response.ok) return false;
      const file = await response.json();
      return !file.trashed;
    } catch {
      return false;
    }
  }

  /**
   * Clear all cached folder IDs when the root folder was deleted externally.
   * Forces full folder tree recreation on next upload.
   */
  private invalidateAllFolderCaches(): void {
    console.warn('GoogleDriveService: Root folder deleted externally — clearing all cached folder IDs');
    this.folderCache.clear();
    this.saveFolderCache();
    // NOTE: Do NOT remove ROOT_FOLDER_ID or CONFIG_FOLDER_ID here.
    // isConnected() depends on ROOT_FOLDER_ID to detect Drive connection.
    // _ensureFolderPathImpl will find-or-create the root and update these.
    this.cacheValidated = false;
  }

  /**
   * Check if user has connected Google Drive.
   * Pilots: root folder + email (not token expiry — GIS silent refresh handles renewal).
   * Family members: email + connection timestamp (they don't create/store a root folder).
   */
  isConnected(): boolean {
    const userEmail = localStorage.getItem(STORAGE_KEYS.USER_EMAIL);
    if (!userEmail) return false;

    const rootFolderId = localStorage.getItem(STORAGE_KEYS.ROOT_FOLDER_ID);
    if (rootFolderId) return true;

    // Family members skip folder creation — check for connection timestamp instead
    const isFamilyMember = localStorage.getItem('lucidDock_userType') === 'family';
    const connectedAt = localStorage.getItem(STORAGE_KEYS.CONNECTED_AT);
    return isFamilyMember && !!connectedAt;
  }

  /**
   * Validate the Google Drive connection by verifying the token is actually usable.
   * This makes an actual API call to verify the token works.
   * Returns true if connected and token is valid, false otherwise.
   * Dispatches 'googleDriveAuthError' event when auth fails.
   */
  async validateConnection(): Promise<boolean> {
    if (!this.isConnected()) {
      return false;
    }

    try {
      const token = await this.getValidAccessToken();
      if (!token) {
        this.dispatchAuthError('Token refresh failed. Please reconnect Google Drive.');
        return false;
      }

      // Lightweight API call to verify the token works (5-second timeout)
      const response = await this.driveFetch(
        `${GOOGLE_DRIVE_API_BASE}/about?fields=user`,
        { headers: { Authorization: `Bearer ${token}` } },
        DRIVE_VALIDATION_TIMEOUT
      );

      if (!response.ok) {
        // 401/403 already dispatched by driveFetch
        return false;
      }

      return true;
    } catch (error) {
      console.error('Google Drive connection validation failed:', error);
      this.dispatchAuthError('Unable to connect to Google Drive. Please check your connection.');
      return false;
    }
  }

  /**
   * Get connected user email
   */
  getConnectedEmail(): string | null {
    return localStorage.getItem(STORAGE_KEYS.USER_EMAIL);
  }

  /**
   * Get connection timestamp
   */
  getConnectedAt(): string | null {
    return localStorage.getItem(STORAGE_KEYS.CONNECTED_AT);
  }

  /**
   * Get the root LucidDock folder ID
   */
  getRootFolderId(): string | null {
    return localStorage.getItem(STORAGE_KEYS.ROOT_FOLDER_ID);
  }

  /**
   * Get current storage settings for profile
   */
  getStorageSettings(): GoogleDriveSettings {
    return {
      connected: this.isConnected(),
      connectedEmail: this.getConnectedEmail() || undefined,
      rootFolderId: this.getRootFolderId() || undefined,
      connectedAt: this.getConnectedAt() || undefined,
      folderCache: Object.fromEntries(this.folderCache),
    };
  }

  /**
   * Connect Google Drive using an access_token obtained from useGoogleLogin (implicit flow).
   * Token refresh is handled silently via Google Identity Services (GIS).
   * @param accessToken - The access token from Google OAuth
   * @param expiresIn - Token expiry time in seconds (default 3600)
   * @param expectedEmail - The email of the logged-in LucidDock user (must match Google account)
   */
  async connectWithAccessToken(
    accessToken: string,
    expiresIn: number = 3600,
    expectedEmail?: string
  ): Promise<{ success: boolean; error?: string }> {
    try {
      // Store the access token
      localStorage.setItem(STORAGE_KEYS.ACCESS_TOKEN, accessToken);

      // Calculate and store expiry time
      const expiryTime = Date.now() + expiresIn * 1000;
      localStorage.setItem(STORAGE_KEYS.TOKEN_EXPIRY, expiryTime.toString());

      return this.completeConnection(expectedEmail);
    } catch (error) {
      console.error('Google Drive connect with access token error:', error);
      this.disconnect();
      return { success: false, error: 'Failed to complete authentication' };
    }
  }

  /**
   * Shared setup after tokens are stored: validate email, create folders, save timestamp.
   * Family members skip folder creation — they access the Pilot's shared Drive folder.
   */
  private async completeConnection(
    expectedEmail?: string,
  ): Promise<{ success: boolean; error?: string }> {
    // Fetch user info to get email
    const googleEmail = await this.fetchUserInfoAndGetEmail();

    // Validate that the Google account matches the LucidDock user
    if (expectedEmail && googleEmail && googleEmail.toLowerCase() !== expectedEmail.toLowerCase()) {
      this.disconnect();
      return {
        success: false,
        error: `Please sign in with your LucidDock account (${expectedEmail}). You signed in with ${googleEmail}.`,
      };
    }

    // Family members: only need a valid token — folder access is via the Pilot's shared folder.
    // Skip creating folders in the family member's own Drive.
    const isFamilyMember = localStorage.getItem('lucidDock_userType') === 'family';

    if (!isFamilyMember) {
      // Pilot: create or find the root LucidDock folder
      const rootFolderId = await this.ensureRootFolder();
      if (!rootFolderId) {
        this.disconnect();
        return {
          success: false,
          error: 'Failed to create LucidDock folder in Google Drive. Please try again.',
        };
      }

      localStorage.setItem(STORAGE_KEYS.ROOT_FOLDER_ID, rootFolderId);

      // Create the hidden config folder (.luciddock) - confirms setup complete
      const configFolderId = await this.ensureConfigFolder(rootFolderId);
      if (configFolderId) {
        localStorage.setItem(STORAGE_KEYS.CONFIG_FOLDER_ID, configFolderId);
      }
    }

    // Save connection timestamp and ensure storage preference is set
    localStorage.setItem(STORAGE_KEYS.CONNECTED_AT, new Date().toISOString());
    localStorage.setItem('lucidDock_storageProvider', 'google-drive');

    return { success: true };
  }

  private dispatchAuthError(message: string): void {
    window.dispatchEvent(new CustomEvent('googleDriveAuthError', {
      detail: { error: message },
    }));
  }

  /**
   * Get access token from localStorage.
   * Checks token expiry — if expired, clears it and signals reconnect.
   * This prevents cascading 401 errors and shows the reconnect prompt immediately.
   */
  async getValidAccessToken(): Promise<string | null> {
    const accessToken = localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN);

    if (accessToken) {
      // Check expiry — clear if expired (with 60s buffer to avoid edge cases)
      const expiryStr = localStorage.getItem(STORAGE_KEYS.TOKEN_EXPIRY);
      if (expiryStr) {
        const expiryTime = parseInt(expiryStr, 10);
        if (Date.now() > expiryTime - 60_000) {
          this.clearStoredToken();
          this.dispatchAuthError('Google Drive session expired. Please reconnect.');
          return null;
        }
      }
      return accessToken;
    }

    // No token but user was previously connected — signal reconnect
    if (this.isConnected()) {
      this.dispatchAuthError('Google Drive session expired. Please reconnect.');
    }
    return null;
  }

  /**
   * Whether a valid (non-expired) access token exists.
   * Used by UI to distinguish "connected + valid" from "connected but expired".
   */
  hasValidToken(): boolean {
    const token = localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN);
    if (!token) return false;
    const expiryStr = localStorage.getItem(STORAGE_KEYS.TOKEN_EXPIRY);
    if (!expiryStr) return true; // No expiry stored — assume valid
    return Date.now() < parseInt(expiryStr, 10) - 60_000;
  }

  /**
   * Fetch user info after successful auth and return the email
   */
  private async fetchUserInfoAndGetEmail(): Promise<string | null> {
    const accessToken = await this.getValidAccessToken();
    if (!accessToken) return null;

    try {
      const response = await this.driveFetch(
        'https://www.googleapis.com/oauth2/v2/userinfo',
        { headers: { Authorization: `Bearer ${accessToken}` } },
        DRIVE_VALIDATION_TIMEOUT
      );

      if (response.ok) {
        const userInfo = await response.json();
        const email = userInfo.email || '';
        localStorage.setItem(STORAGE_KEYS.USER_EMAIL, email);
        return email;
      }
      return null;
    } catch (error) {
      console.error('Failed to fetch user info:', error);
      return null;
    }
  }

  /**
   * Store a fresh access token without triggering full connection flow.
   * Used during account deletion re-auth — we need a valid Drive token
   * for cleanup, but the connection (root folder, email) is already set up.
   */
  storeAccessToken(accessToken: string, expiresIn: number = 3600): void {
    localStorage.setItem(STORAGE_KEYS.ACCESS_TOKEN, accessToken);
    const expiryTime = Date.now() + expiresIn * 1000;
    localStorage.setItem(STORAGE_KEYS.TOKEN_EXPIRY, expiryTime.toString());
  }

  /**
   * Clear stored token only (called when a 401 is detected).
   * Preserves connection state (email, root folder) so reconnect is seamless.
   */
  clearStoredToken(): void {
    localStorage.removeItem(STORAGE_KEYS.ACCESS_TOKEN);
    localStorage.removeItem(STORAGE_KEYS.TOKEN_EXPIRY);
  }

  /**
   * Disconnect Google Drive
   */
  disconnect(): void {
    localStorage.removeItem(STORAGE_KEYS.ACCESS_TOKEN);
    localStorage.removeItem(STORAGE_KEYS.REFRESH_TOKEN);
    localStorage.removeItem(STORAGE_KEYS.TOKEN_EXPIRY);
    localStorage.removeItem(STORAGE_KEYS.USER_EMAIL);
    localStorage.removeItem(STORAGE_KEYS.ROOT_FOLDER_ID);
    localStorage.removeItem(STORAGE_KEYS.CONFIG_FOLDER_ID);
    localStorage.removeItem(STORAGE_KEYS.FOLDER_CACHE);
    localStorage.removeItem(STORAGE_KEYS.CONNECTED_AT);
    this.folderCache.clear();
  }

  /**
   * Find or create the root "LucidDock" folder
   * Handles duplicates by keeping only the most recent one
   */
  private async ensureRootFolder(): Promise<string | null> {
    const accessToken = await this.getValidAccessToken();
    if (!accessToken) return null;

    try {
      // Check if we have a cached folder ID from localStorage
      const cachedFolderId = localStorage.getItem(STORAGE_KEYS.ROOT_FOLDER_ID);
      if (cachedFolderId) {
        // Verify the cached folder still exists
        try {
          const verifyResponse = await this.driveFetch(
            `${GOOGLE_DRIVE_API_BASE}/files/${cachedFolderId}?fields=id,name,trashed`,
            { headers: { Authorization: `Bearer ${accessToken}` } }
          );
          
          if (verifyResponse.ok) {
            const folder = await verifyResponse.json();
            if (!folder.trashed) {
              // Cached folder is valid, use it
              this.folderCache.set('LucidDock', cachedFolderId);
              this.saveFolderCache();
              return cachedFolderId;
            }
          }
        } catch (error) {
          console.warn('GoogleDriveService: Cached folder no longer exists, will search for or create new one');
        }
      }

      // Search for ALL existing LucidDock folders in root
      const searchResponse = await this.driveFetch(
        `${GOOGLE_DRIVE_API_BASE}/files?` +
          new URLSearchParams({
            q: `name='LucidDock' and mimeType='${MIME_TYPES.FOLDER}' and 'root' in parents and trashed=false`,
            fields: 'files(id,name,createdTime,modifiedTime)',
            spaces: 'drive',
          }),
        { headers: { Authorization: `Bearer ${accessToken}` } }
      );

      if (searchResponse.ok) {
        const data = await searchResponse.json();
        if (data.files && data.files.length > 0) {
          // If multiple folders exist, use the most recently modified one
          let selectedFolder;
          
          if (data.files.length > 1) {
            console.warn(`GoogleDriveService: Found ${data.files.length} LucidDock folders, using most recent`);
            
            // Sort by modifiedTime descending (most recent first)
            const sortedFolders = data.files.sort((a: any, b: any) => {
              return new Date(b.modifiedTime).getTime() - new Date(a.modifiedTime).getTime();
            });
            
            selectedFolder = sortedFolders[0];
          } else {
            // Single folder found
            selectedFolder = data.files[0];
          }
          
          const folderId = selectedFolder.id;
          this.folderCache.set('LucidDock', folderId);
          this.saveFolderCache();
          localStorage.setItem(STORAGE_KEYS.ROOT_FOLDER_ID, folderId);
          return folderId;
        }
      } else {
        console.error('GoogleDriveService: Failed to search for LucidDock folder:', searchResponse.status);
      }

      // Create new LucidDock folder
      const createResponse = await this.driveFetch(
        `${GOOGLE_DRIVE_API_BASE}/files`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${accessToken}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            name: 'LucidDock',
            mimeType: MIME_TYPES.FOLDER,
            parents: ['root'],
          }),
        }
      );

      if (createResponse.ok) {
        const folder = await createResponse.json();
        this.folderCache.set('LucidDock', folder.id);
        this.saveFolderCache();
        localStorage.setItem(STORAGE_KEYS.ROOT_FOLDER_ID, folder.id);
        return folder.id;
      }

      // Log the error for debugging
      const errorText = await createResponse.text();
      console.error('GoogleDriveService: Failed to create LucidDock folder:', createResponse.status, errorText);
      return null;
    } catch (error) {
      console.error('Failed to ensure root folder:', error);
      return null;
    }
  }

  /**
   * Find or create the hidden config folder ".luciddock" inside the root folder
   * This folder stores encrypted profile data and metadata
   */
  private async ensureConfigFolder(rootFolderId: string): Promise<string | null> {
    const accessToken = await this.getValidAccessToken();
    if (!accessToken) return null;

    try {
      // Search for existing .luciddock folder
      const searchResponse = await this.driveFetch(
        `${GOOGLE_DRIVE_API_BASE}/files?` +
          new URLSearchParams({
            q: `name='.luciddock' and mimeType='${MIME_TYPES.FOLDER}' and '${rootFolderId}' in parents and trashed=false`,
            fields: 'files(id,name)',
            spaces: 'drive',
          }),
        { headers: { Authorization: `Bearer ${accessToken}` } }
      );

      if (searchResponse.ok) {
        const data = await searchResponse.json();
        if (data.files && data.files.length > 0) {
          // Found existing folder
          const folderId = data.files[0].id;
          this.folderCache.set('LucidDock/.luciddock', folderId);
          this.saveFolderCache();
          return folderId;
        }
      }

      // Create new .luciddock folder
      const createResponse = await this.driveFetch(
        `${GOOGLE_DRIVE_API_BASE}/files`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${accessToken}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            name: '.luciddock',
            mimeType: MIME_TYPES.FOLDER,
            parents: [rootFolderId],
          }),
        }
      );

      if (createResponse.ok) {
        const folder = await createResponse.json();
        this.folderCache.set('LucidDock/.luciddock', folder.id);
        this.saveFolderCache();
        return folder.id;
      }

      return null;
    } catch (error) {
      console.error('Failed to ensure config folder:', error);
      return null;
    }
  }

  /**
   * Get the config folder ID (.luciddock)
   */
  getConfigFolderId(): string | null {
    return localStorage.getItem(STORAGE_KEYS.CONFIG_FOLDER_ID);
  }

  /**
   * Find or create a folder by path
   * Uses caching to minimize API calls
   */
  async ensureFolderPath(path: string): Promise<string | null> {
    // Check cache first
    if (this.folderCache.has(path)) {
      // Validate root folder once per session to detect external deletion
      if (!this.cacheValidated) {
        const rootValid = await this.validateRootFolder();
        if (rootValid) {
          this.cacheValidated = true;
        } else {
          // Root was deleted externally — clear stale cache and recreate
          this.invalidateAllFolderCaches();
        }
      }

      // If root is valid, validate the leaf (target) folder before returning
      if (this.cacheValidated) {
        const leafId = this.folderCache.get(path);
        if (leafId) {
          const leafValid = await this.validateFolderExists(leafId);
          if (leafValid) return leafId;
          // Leaf was deleted externally — clear stale cache for this branch
          this.invalidateBranchCache(path);
        }
      }
    }

    // Serialize concurrent calls for the same path to prevent duplicate folders
    const existing = this.folderLocks.get(path);
    if (existing) return existing;

    const promise = this._ensureFolderPathImpl(path);
    this.folderLocks.set(path, promise);
    try {
      return await promise;
    } finally {
      this.folderLocks.delete(path);
    }
  }

  /**
   * Check if a specific folder ID still exists and isn't trashed.
   */
  private async validateFolderExists(folderId: string): Promise<boolean> {
    const accessToken = await this.getValidAccessToken();
    if (!accessToken) return false;
    try {
      const response = await this.driveFetch(
        `${GOOGLE_DRIVE_API_BASE}/files/${folderId}?fields=id,trashed`,
        { headers: { Authorization: `Bearer ${accessToken}` } },
        DRIVE_VALIDATION_TIMEOUT
      );
      if (!response.ok) return false;
      const file = await response.json();
      return !file.trashed;
    } catch {
      return false;
    }
  }

  /**
   * Clear cached folder IDs for a path and all its children.
   * Used when an intermediate folder was externally deleted.
   */
  private invalidateBranchCache(branchPath: string): void {
    // Clear the target path and any child paths that depend on it
    for (const key of Array.from(this.folderCache.keys())) {
      if (key === branchPath || key.startsWith(branchPath + '/')) {
        this.folderCache.delete(key);
      }
    }
    // Also clear parent paths up to root — they might point to stale parents
    const parts = branchPath.split('/');
    for (let i = parts.length - 1; i >= 2; i--) {
      // Keep 'LucidDock' and 'LucidDock/{Profile}' — clear entity-level and below
      this.folderCache.delete(parts.slice(0, i).join('/'));
    }
    this.saveFolderCache();
  }

  private async _ensureFolderPathImpl(path: string): Promise<string | null> {
    const accessToken = await this.getValidAccessToken();
    if (!accessToken) return null;

    // Validate cache if not done this session (covers case where full path
    // isn't cached but parent paths are — they'd point to trashed folders)
    if (!this.cacheValidated && this.folderCache.size > 0) {
      const rootValid = await this.validateRootFolder();
      if (rootValid) {
        this.cacheValidated = true;
      } else {
        this.invalidateAllFolderCaches();
      }
    }

    const parentPaths = getAllParentPaths(path);
    let parentId = 'root';

    for (const currentPath of parentPaths) {
      // Check cache first
      if (this.folderCache.has(currentPath)) {
        parentId = this.folderCache.get(currentPath) || 'root';
        continue;
      }

      // Wait for concurrent creation of this sub-path (skip own lock to avoid deadlock)
      if (currentPath !== path) {
        const subLock = this.folderLocks.get(currentPath);
        if (subLock) {
          const result = await subLock;
          if (result) { parentId = result; continue; }
        }
      }

      const folderName = currentPath.split('/').pop() || currentPath;

      try {
        // Search for existing folder
        const searchResponse = await this.driveFetch(
          `${GOOGLE_DRIVE_API_BASE}/files?` +
            new URLSearchParams({
              q: `name='${folderName}' and mimeType='${MIME_TYPES.FOLDER}' and '${parentId}' in parents and trashed=false`,
              fields: 'files(id,name)',
              spaces: 'drive',
            }),
          { headers: { Authorization: `Bearer ${accessToken}` } }
        );

        if (!searchResponse.ok) {
          console.error('Folder search failed:', searchResponse.status, currentPath);
          return null;
        }

        const data = await searchResponse.json();
        if (data.files && data.files.length > 0) {
          parentId = data.files[0].id;
          this.folderCache.set(currentPath, parentId);
          // Restore ROOT_FOLDER_ID if the root was recreated externally
          if (currentPath === 'LucidDock') {
            localStorage.setItem(STORAGE_KEYS.ROOT_FOLDER_ID, parentId);
            this.cacheValidated = true;
          }
          continue;
        }

        // Create new folder
        const createResponse = await this.driveFetch(
          `${GOOGLE_DRIVE_API_BASE}/files`,
          {
            method: 'POST',
            headers: {
              Authorization: `Bearer ${accessToken}`,
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              name: folderName,
              mimeType: MIME_TYPES.FOLDER,
              parents: [parentId],
            }),
          }
        );

        if (createResponse.ok) {
          const folder = await createResponse.json();
          parentId = folder.id;
          this.folderCache.set(currentPath, parentId);
          // Restore ROOT_FOLDER_ID after recreating the root folder
          if (currentPath === 'LucidDock') {
            localStorage.setItem(STORAGE_KEYS.ROOT_FOLDER_ID, parentId);
            this.cacheValidated = true;
          }
        } else {
          // Create failed — parent might be stale. Clear cache and retry once.
          console.warn('Folder create failed, retrying with fresh cache:', currentPath);
          this.invalidateAllFolderCaches();
          return this._ensureFolderPathRetry(path, accessToken);
        }
      } catch (error) {
        // Timeout (AbortError) or network glitch — retry once with fresh cache
        console.warn('Error ensuring folder path, retrying with fresh cache:', currentPath, error);
        this.invalidateAllFolderCaches();
        return this._ensureFolderPathRetry(path, accessToken);
      }
    }

    this.saveFolderCache();
    return parentId;
  }

  /**
   * Retry folder path creation from scratch (no cache).
   * Called when the primary attempt fails due to stale parent IDs.
   */
  private async _ensureFolderPathRetry(path: string, accessToken: string): Promise<string | null> {
    const parentPaths = getAllParentPaths(path);
    let parentId = 'root';

    for (const currentPath of parentPaths) {
      const folderName = currentPath.split('/').pop() || currentPath;
      try {
        const searchResponse = await this.driveFetch(
          `${GOOGLE_DRIVE_API_BASE}/files?` +
            new URLSearchParams({
              q: `name='${folderName}' and mimeType='${MIME_TYPES.FOLDER}' and '${parentId}' in parents and trashed=false`,
              fields: 'files(id,name)',
              spaces: 'drive',
            }),
          { headers: { Authorization: `Bearer ${accessToken}` } }
        );
        if (!searchResponse.ok) return null;

        const data = await searchResponse.json();
        if (data.files && data.files.length > 0) {
          parentId = data.files[0].id;
        } else {
          const createResponse = await this.driveFetch(
            `${GOOGLE_DRIVE_API_BASE}/files`,
            {
              method: 'POST',
              headers: {
                Authorization: `Bearer ${accessToken}`,
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({
                name: folderName,
                mimeType: MIME_TYPES.FOLDER,
                parents: [parentId],
              }),
            }
          );
          if (!createResponse.ok) return null;
          const folder = await createResponse.json();
          parentId = folder.id;
        }
        this.folderCache.set(currentPath, parentId);
        if (currentPath === 'LucidDock') {
          localStorage.setItem(STORAGE_KEYS.ROOT_FOLDER_ID, parentId);
          this.cacheValidated = true;
        }
      } catch (error) {
        console.error('Folder path retry failed:', currentPath, error);
        return null;
      }
    }

    this.saveFolderCache();
    return parentId;
  }

  /**
   * Upload a file to Google Drive.
   * Validates token upfront and uses per-request timeouts to fail fast.
   */
  async uploadFile(file: File, context: UploadContext): Promise<StorageUploadResult | null> {
    const accessToken = await this.getValidAccessToken();
    if (!accessToken) {
      console.error('No valid access token for Google Drive');
      return null;
    }

    // Fast token validation — catches expired tokens quickly instead of hanging
    const tokenCheck = await this.driveFetch(
      `${GOOGLE_DRIVE_API_BASE}/about?fields=kind`,
      { headers: { Authorization: `Bearer ${accessToken}` } },
      DRIVE_VALIDATION_TIMEOUT
    ).catch(() => null);

    if (!tokenCheck || !tokenCheck.ok) {
      this.clearStoredToken();
      return null;
    }

    try {
      // Build the folder path and ensure it exists
      const folderPath = buildFolderPath(context);
      const folderId = await this.ensureFolderPath(folderPath);

      if (!folderId) {
        console.error('Failed to create folder structure');
        return null;
      }

      // Generate unique file name
      const uniqueFileName = generateUniqueFileName(context.fileName);

      // Build metadata with app properties for lookup and attribution
      const metadata: GoogleDriveFileMetadata = {
        lucidDock: 'true',
        lucidDock_profileId: context.profileId,
        lucidDock_entityType: context.entityType,
        lucidDock_entityId: context.entityId,
        lucidDock_uploadedAt: new Date().toISOString(),
        ...(context.uploadedBy && { lucidDock_uploadedBy: context.uploadedBy }),
        ...(context.uploadedByEmail && { lucidDock_uploadedByEmail: context.uploadedByEmail }),
      };

      const metadataBody = {
        name: uniqueFileName,
        parents: [folderId],
        appProperties: metadata,
      };

      // Resumable upload: POST metadata to get upload URI, then PUT file content.
      // More reliable than multipart — no encoding issues, works for any file size.
      const initResponse = await this.driveFetch(
        `${GOOGLE_DRIVE_UPLOAD_API}/files?uploadType=resumable&fields=id,name,webViewLink,webContentLink,size`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${accessToken}`,
            'Content-Type': 'application/json; charset=UTF-8',
          },
          body: JSON.stringify(metadataBody),
        }
      );

      if (!initResponse.ok) {
        console.error('Failed to initiate upload:', await initResponse.text());
        return null;
      }

      const uploadUri = initResponse.headers.get('Location');
      if (!uploadUri) {
        console.error('No upload URI returned from Google Drive');
        return null;
      }

      // Upload file content (auth is baked into the resumable URI)
      const uploadResponse = await this.driveFetch(
        uploadUri,
        {
          method: 'PUT',
          headers: { 'Content-Type': file.type || 'application/octet-stream' },
          body: file,
        },
        DRIVE_UPLOAD_TIMEOUT
      );

      if (!uploadResponse.ok) {
        console.error('Failed to upload file:', await uploadResponse.text());
        return null;
      }

      const uploadedFile = await uploadResponse.json();

      // Make the file publicly accessible (via link)
      await this.setFilePermission(uploadedFile.id);

      // Get the direct download URL
      const downloadUrl = await this.getDownloadUrl(uploadedFile.id);

      return {
        url: downloadUrl || uploadedFile.webContentLink || uploadedFile.webViewLink,
        storagePath: `${folderPath}/${uniqueFileName}`,
        provider: 'google-drive' as StorageProvider,
        googleDriveFileId: uploadedFile.id,
        size: parseInt(uploadedFile.size || '0', 10) || file.size,
        contentType: file.type || 'application/octet-stream',
      };
    } catch (error) {
      console.error('Error uploading file to Google Drive:', error);
      return null;
    }
  }

  /**
   * Set file permission to anyone with link can view
   */
  private async setFilePermission(fileId: string): Promise<boolean> {
    const accessToken = await this.getValidAccessToken();
    if (!accessToken) return false;

    try {
      const response = await this.driveFetch(
        `${GOOGLE_DRIVE_API_BASE}/files/${fileId}/permissions`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${accessToken}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            role: 'reader',
            type: 'anyone',
          }),
        }
      );

      return response.ok;
    } catch (error) {
      console.error('Error setting file permission:', error);
      return false;
    }
  }

  /**
   * Get embeddable URL for a file.
   * Returns a thumbnail URL that works for publicly shared files.
   */
  async getDownloadUrl(fileId: string): Promise<string | null> {
    return `https://drive.google.com/thumbnail?id=${fileId}&sz=w2000`;
  }

  /**
   * Fetch file content as a blob URL using authenticated API access.
   * This is the most reliable way to display Google Drive images — doesn't
   * depend on public sharing URLs which are unreliable with drive.file scope.
   */
  async getFileBlobUrl(fileId: string): Promise<string | null> {
    const accessToken = await this.getValidAccessToken();
    if (!accessToken) return null;

    try {
      const response = await this.driveFetch(
        `${GOOGLE_DRIVE_API_BASE}/files/${fileId}?alt=media`,
        { headers: { Authorization: `Bearer ${accessToken}` } }
      );

      if (!response.ok) return null;

      const blob = await response.blob();
      return URL.createObjectURL(blob);
    } catch (error) {
      console.error('Error fetching file blob:', error);
      return null;
    }
  }

  /**
   * Rename a file in Google Drive
   */
  async renameFile(fileId: string, newName: string): Promise<boolean> {
    const accessToken = await this.getValidAccessToken();
    if (!accessToken) return false;

    try {
      const response = await this.driveFetch(
        `${GOOGLE_DRIVE_API_BASE}/files/${fileId}`,
        {
          method: 'PATCH',
          headers: {
            Authorization: `Bearer ${accessToken}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ name: newName }),
        }
      );

      return response.ok;
    } catch (error) {
      console.error('Error renaming file in Google Drive:', error);
      return false;
    }
  }

  /**
   * Move a file to a different folder in Google Drive.
   * Uses the PATCH endpoint with addParents/removeParents query params.
   */
  async moveFile(fileId: string, newFolderId: string): Promise<boolean> {
    const accessToken = await this.getValidAccessToken();
    if (!accessToken) return false;

    try {
      // Get current parents so we can remove them
      const getResp = await this.driveFetch(
        `${GOOGLE_DRIVE_API_BASE}/files/${fileId}?fields=parents`,
        { headers: { Authorization: `Bearer ${accessToken}` } }
      );
      if (!getResp.ok) return false;
      const { parents } = await getResp.json();
      const previousParents = (parents || []).join(',');

      const response = await this.driveFetch(
        `${GOOGLE_DRIVE_API_BASE}/files/${fileId}?addParents=${newFolderId}&removeParents=${previousParents}`,
        {
          method: 'PATCH',
          headers: {
            Authorization: `Bearer ${accessToken}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({}),
        }
      );

      return response.ok;
    } catch (error) {
      console.error('Error moving file in Google Drive:', error);
      return false;
    }
  }

  /**
   * Delete a file from Google Drive
   */
  async deleteFile(fileId: string): Promise<boolean> {
    const accessToken = await this.getValidAccessToken();
    if (!accessToken) return false;

    try {
      const response = await this.driveFetch(
        `${GOOGLE_DRIVE_API_BASE}/files/${fileId}`,
        {
          method: 'DELETE',
          headers: { Authorization: `Bearer ${accessToken}` },
        }
      );

      // 204 No Content or 404 Not Found (already deleted) are both success
      return response.ok || response.status === 404;
    } catch (error) {
      console.error('Error deleting file from Google Drive:', error);
      return false;
    }
  }

  /**
   * Check if a file exists in Google Drive
   */
  async fileExists(fileId: string): Promise<boolean> {
    const accessToken = await this.getValidAccessToken();
    if (!accessToken) return false;

    try {
      const response = await this.driveFetch(
        `${GOOGLE_DRIVE_API_BASE}/files/${fileId}?fields=id,trashed`,
        { headers: { Authorization: `Bearer ${accessToken}` } }
      );

      if (response.ok) {
        const file = await response.json();
        return !file.trashed;
      }

      return false;
    } catch (error) {
      console.error('Error checking file existence:', error);
      return false;
    }
  }

  /**
   * Get storage usage from Google Drive (files uploaded by this app only)
   */
  async getStorageUsage(): Promise<{ totalBytes: number; fileCount: number }> {
    const accessToken = await this.getValidAccessToken();
    if (!accessToken) return { totalBytes: 0, fileCount: 0 };

    try {
      // Query files with our app properties
      const response = await this.driveFetch(
        `${GOOGLE_DRIVE_API_BASE}/files?` +
          new URLSearchParams({
            q: `appProperties has { key='lucidDock' and value='true' } and trashed=false`,
            fields: 'files(id,size)',
            pageSize: '1000',
            spaces: 'drive',
          }),
        { headers: { Authorization: `Bearer ${accessToken}` } }
      );

      if (response.ok) {
        const data = await response.json();
        const files = data.files || [];

        let totalBytes = 0;
        for (const file of files) {
          totalBytes += parseInt(file.size || '0', 10);
        }

        return {
          totalBytes,
          fileCount: files.length,
        };
      }

      return { totalBytes: 0, fileCount: 0 };
    } catch (error) {
      console.error('Error getting storage usage:', error);
      return { totalBytes: 0, fileCount: 0 };
    }
  }

  /**
   * List files in a folder (for debugging/admin)
   */
  async listFilesInFolder(folderId: string): Promise<DriveFile[]> {
    const accessToken = await this.getValidAccessToken();
    if (!accessToken) return [];

    try {
      const response = await this.driveFetch(
        `${GOOGLE_DRIVE_API_BASE}/files?` +
          new URLSearchParams({
            q: `'${folderId}' in parents and trashed=false`,
            fields: 'files(id,name,mimeType,size,webViewLink,webContentLink,thumbnailLink,appProperties)',
            spaces: 'drive',
          }),
        { headers: { Authorization: `Bearer ${accessToken}` } }
      );

      if (response.ok) {
        const data = await response.json();
        return data.files || [];
      }

      return [];
    } catch (error) {
      console.error('Error listing files:', error);
      return [];
    }
  }

  /**
   * Clear folder cache (useful when debugging or after errors)
   */
  clearFolderCache(): void {
    this.folderCache.clear();
    localStorage.removeItem(STORAGE_KEYS.FOLDER_CACHE);
  }

  // ============================================================================
  // IMMEDIATE CO-PILOT FEATURE
  // ============================================================================

  /**
   * Get permissions for a folder (used for Immediate Co-Pilot verification)
   *
   * IMPORTANT: This uses the Pilot's OAuth token and only works while Pilot is active.
   * After Pilot inactivity, verification stops - but Google's sharing continues independently.
   *
   * @param folderId - The Google Drive folder ID to check permissions for
   * @returns Permissions list or error
   */
  async getFolderPermissions(folderId: string): Promise<FolderPermissionsResult> {
    const accessToken = await this.getValidAccessToken();
    if (!accessToken) {
      return { success: false, error: 'No valid access token' };
    }

    try {
      const response = await this.driveFetch(
        `${GOOGLE_DRIVE_API_BASE}/files/${folderId}/permissions?` +
          new URLSearchParams({
            fields: 'permissions(id,type,emailAddress,role,deleted)',
          }),
        { headers: { Authorization: `Bearer ${accessToken}` } }
      );

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Failed to get folder permissions:', errorText);
        return { success: false, error: `API error: ${response.status}` };
      }

      const data = await response.json();
      return {
        success: true,
        permissions: data.permissions || [],
      };
    } catch (error) {
      console.error('Error getting folder permissions:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error',
      };
    }
  }

  /**
   * Check Pre-shared Drive Access status for a specific member
   *
   * Verification logic:
   * - permission.deleted === false (not revoked)
   * - permission.emailAddress matches memberEmail (case-insensitive)
   * - permission.role matches expected role (writer for editor, reader for viewer)
   * - If role matches → ACCESS CONFIRMED
   * - If permission exists with any active role → ACCESS CONFIRMED (user may have given more/less access)
   * - If permission exists but has other state → INVITE SENT (pending)
   * - If no matching permission → NOT SHARED
   *
   * @param memberEmail - The family member's Google email to check for
   * @param expectedRole - The role we expect ('editor' = writer, 'viewer' = reader)
   * @returns The sharing status and detected role if any
   */
  async checkPreSharedDriveStatus(
    memberEmail: string,
    _expectedRole: PreSharedDriveRole // Kept for API consistency; may be used for strict validation in future
  ): Promise<{
    status: PreSharedDriveStatus;
    detectedRole?: 'editor' | 'viewer';
    error?: string;
  }> {
    const rootFolderId = this.getRootFolderId();
    if (!rootFolderId) {
      return { status: 'not_shared', error: 'LucidDock folder not found' };
    }

    const result = await this.getFolderPermissions(rootFolderId);
    if (!result.success || !result.permissions) {
      return { status: 'not_shared', error: result.error };
    }

    // Find permission for member's email
    const memberPermission = result.permissions.find(
      (p) =>
        p.emailAddress?.toLowerCase() === memberEmail.toLowerCase() &&
        p.deleted !== true
    );

    if (!memberPermission) {
      return { status: 'not_shared' };
    }

    // Check if they have active access
    // writer = Editor (can add/modify files)
    // reader = Viewer (read only)
    if (memberPermission.role === 'writer') {
      return { status: 'access_confirmed', detectedRole: 'editor' };
    }

    if (memberPermission.role === 'reader') {
      return { status: 'access_confirmed', detectedRole: 'viewer' };
    }

    // Permission exists but not fully active yet (could be pending invite)
    return { status: 'invite_sent' };
  }

  /**
   * @deprecated Use checkPreSharedDriveStatus instead
   * Kept for backwards compatibility
   */
  async checkImmediateAccessStatus(
    memberEmail: string,
    expectedRole: PreSharedDriveRole
  ): Promise<{
    status: PreSharedDriveStatus;
    detectedRole?: 'editor' | 'viewer';
    error?: string;
  }> {
    return this.checkPreSharedDriveStatus(memberEmail, expectedRole);
  }

  /**
   * @deprecated Use checkPreSharedDriveStatus instead
   * Kept for backwards compatibility
   */
  async checkImmediateCoPilotStatus(spouseEmail: string): Promise<{
    status: PreSharedDriveStatus;
    error?: string;
  }> {
    return this.checkPreSharedDriveStatus(spouseEmail, 'editor');
  }

  /**
   * Get the URL to open Google Drive folder for sharing
   * Opens the folder in Google Drive where user can click Share
   */
  getShareFolderUrl(): string | null {
    const rootFolderId = this.getRootFolderId();
    if (!rootFolderId) return null;
    return `https://drive.google.com/drive/folders/${rootFolderId}`;
  }

  // ============================================================================
  // FOLDER ACCESS MANAGEMENT (FAMILY SHARING)
  // Automates Google Drive permissions for Pro plan family members
  // ============================================================================

  /**
   * Grant a user access to a Google Drive folder.
   * Used when Pilot invites a family member on Pro plan — automatically shares
   * the LucidDock folder so the member can access data via their own Google token.
   *
   * @param folderId - The folder to share (typically the LucidDock root folder)
   * @param email - The family member's Google email
   * @param role - 'writer' for Co-Pilot (editor), 'reader' for Passenger (viewer)
   * @returns The permission ID (for later revocation) or null on failure
   */
  async grantFolderAccess(
    folderId: string,
    email: string,
    role: 'reader' | 'writer'
  ): Promise<{ permissionId: string | null; error?: string }> {
    const accessToken = await this.getValidAccessToken();
    if (!accessToken) {
      return { permissionId: null, error: 'Drive session expired' };
    }

    const permBody = JSON.stringify({ type: 'user', role, emailAddress: email });
    const permHeaders = {
      Authorization: `Bearer ${accessToken}`,
      'Content-Type': 'application/json',
    };

    // Use plain fetch (NOT driveFetch) — a 403 from Permissions API is a scope/permissions
    // issue, NOT an auth error. driveFetch dispatches googleDriveAuthError on any 403 which
    // would incorrectly trigger the reconnect gate and poison the Drive connection state.
    const shareFile = async (fileId: string): Promise<Response> => {
      const controller = new AbortController();
      const timer = setTimeout(() => controller.abort(), DRIVE_API_TIMEOUT);
      try {
        return await fetch(
          `${GOOGLE_DRIVE_API_BASE}/files/${fileId}/permissions?sendNotificationEmail=false`,
          { method: 'POST', headers: permHeaders, body: permBody, signal: controller.signal }
        );
      } finally {
        clearTimeout(timer);
      }
    };

    try {
      // Try sharing the folder directly
      const response = await shareFile(folderId);

      if (response.ok) {
        const data = await response.json();
        return { permissionId: data.id };
      }

      // Check if the error is about children access (drive.file scope limitation)
      const errorText = await response.text();
      console.warn('grantFolderAccess: folder share failed:', response.status, errorText);

      if (response.status === 401) {
        this.dispatchAuthError('Google Drive session expired. Please reconnect.');
        return { permissionId: null, error: 'Drive session expired' };
      }

      const isChildrenError = response.status === 403 &&
        errorText.includes('children');

      if (!isChildrenError) {
        return { permissionId: null, error: 'Could not auto-share Drive folder' };
      }

      // Fallback: share each child of the folder individually (recursive).
      // With drive.file scope, sharing a folder fails if any child lacks per-file auth.
      // Sharing children individually bypasses this check. If a child folder also fails,
      // we recurse into its children (e.g., .luciddock has its own files).
      console.info('grantFolderAccess: falling back to sharing children individually');

      const listChildren = async (parentId: string): Promise<{ id: string; name: string }[]> => {
        const ctrl = new AbortController();
        const t = setTimeout(() => ctrl.abort(), DRIVE_API_TIMEOUT);
        try {
          const resp = await fetch(
            `${GOOGLE_DRIVE_API_BASE}/files?` +
              new URLSearchParams({
                q: `'${parentId}' in parents and trashed=false`,
                fields: 'files(id,name)',
                spaces: 'drive',
              }),
            { headers: { Authorization: `Bearer ${accessToken}` }, signal: ctrl.signal }
          );
          if (!resp.ok) return [];
          const data = await resp.json();
          return data.files || [];
        } finally {
          clearTimeout(t);
        }
      };

      let firstPermissionId: string | null = null;
      let sharedCount = 0;

      // Recursive share: try sharing a file/folder, if the folder fails with
      // "children" error, recurse into its children (max 2 levels deep).
      const shareRecursive = async (fileId: string, name: string, depth: number) => {
        try {
          const resp = await shareFile(fileId);
          if (resp.ok) {
            const data = await resp.json();
            if (!firstPermissionId) firstPermissionId = data.id;
            sharedCount++;
            return;
          }

          // If this child is also a folder with inaccessible children, recurse
          const errText = await resp.text();
          const isNestedChildrenError = resp.status === 403 && errText.includes('children') && depth < 2;
          if (isNestedChildrenError) {
            console.info(`grantFolderAccess: recursing into "${name}" children`);
            const grandchildren = await listChildren(fileId);
            for (const gc of grandchildren) {
              await shareRecursive(gc.id, `${name}/${gc.name}`, depth + 1);
            }
          } else {
            console.warn(`grantFolderAccess: failed to share "${name}":`, resp.status);
          }
        } catch (err) {
          console.warn(`grantFolderAccess: error sharing "${name}":`, err);
        }
      };

      const childFiles = await listChildren(folderId);
      if (childFiles.length === 0) {
        return { permissionId: null, error: 'Could not auto-share Drive folder' };
      }

      for (const child of childFiles) {
        await shareRecursive(child.id, child.name, 0);
      }

      if (firstPermissionId) {
        console.info(`grantFolderAccess: shared ${sharedCount} items individually`);
        return { permissionId: firstPermissionId };
      }

      return { permissionId: null, error: 'Could not auto-share Drive folder' };
    } catch (error) {
      console.error('Error granting folder access:', error);
      return {
        permissionId: null,
        error: 'Could not auto-share Drive folder',
      };
    }
  }

  /**
   * Revoke a user's access to a Google Drive folder.
   * Called when Pilot removes a family member — cleans up Drive permissions.
   *
   * Handles both sharing strategies:
   * - Root folder sharing: permissionId is on the folder itself → direct delete
   * - Child-level sharing (drive.file fallback): permissionId is on a child →
   *   direct delete returns 404, so we scan children and revoke by email
   *
   * @param folderId - The folder to revoke access from
   * @param permissionId - The permission ID returned from grantFolderAccess
   * @param email - The member's email (used for child-level fallback revocation)
   */
  async revokeFolderAccess(folderId: string, permissionId: string, email?: string): Promise<boolean> {
    const accessToken = await this.getValidAccessToken();
    if (!accessToken) return false;

    // Use plain fetch (NOT driveFetch) — a 403 from Permissions API is not an auth error.
    const timedFetch = async (url: string, init: RequestInit = {}): Promise<Response> => {
      const controller = new AbortController();
      const timer = setTimeout(() => controller.abort(), DRIVE_API_TIMEOUT);
      try {
        return await fetch(url, { ...init, signal: controller.signal });
      } finally {
        clearTimeout(timer);
      }
    };

    const headers = { Authorization: `Bearer ${accessToken}` };

    try {
      // Step 1: Try direct delete on the folder
      const response = await timedFetch(
        `${GOOGLE_DRIVE_API_BASE}/files/${folderId}/permissions/${permissionId}`,
        { method: 'DELETE', headers }
      );

      if (response.ok) return true;

      // Step 2: If 404 (permission not on this folder) and we have an email,
      // the permission is on children (drive.file fallback). Revoke from each child.
      if (response.status === 404 && email) {
        console.info('revokeFolderAccess: permission not on root, scanning children for', email);
        return await this.revokeFromChildren(folderId, email, accessToken, timedFetch);
      }

      // 404 without email — treat as already deleted
      return response.status === 404;
    } catch (error) {
      console.error('Error revoking folder access:', error);
      return false;
    }
  }

  /**
   * Revoke a user's permissions from all children of a folder by email (recursive).
   * Mirrors the recursive sharing in grantFolderAccess — checks children and grandchildren.
   */
  private async revokeFromChildren(
    parentFolderId: string,
    email: string,
    accessToken: string,
    timedFetch: (url: string, init?: RequestInit) => Promise<Response>,
    depth = 0
  ): Promise<boolean> {
    if (depth > 2) return false;

    const headers = { Authorization: `Bearer ${accessToken}` };
    const emailLower = email.toLowerCase();

    try {
      // List children of the folder
      const listResponse = await timedFetch(
        `${GOOGLE_DRIVE_API_BASE}/files?` +
          new URLSearchParams({
            q: `'${parentFolderId}' in parents and trashed=false`,
            fields: 'files(id,name)',
            spaces: 'drive',
          }),
        { headers }
      );
      if (!listResponse.ok) return false;

      const listData = await listResponse.json();
      const children: { id: string; name: string }[] = listData.files || [];
      if (children.length === 0) return true;

      let revokedCount = 0;

      for (const child of children) {
        // List permissions on this child
        const permResponse = await timedFetch(
          `${GOOGLE_DRIVE_API_BASE}/files/${child.id}/permissions?fields=permissions(id,emailAddress)`,
          { headers }
        );
        if (!permResponse.ok) {
          // Can't list permissions — try recursing into this child (it might be a folder)
          const nestedRevoked = await this.revokeFromChildren(child.id, email, accessToken, timedFetch, depth + 1);
          if (nestedRevoked) revokedCount++;
          continue;
        }

        const permData = await permResponse.json();
        const permissions: { id: string; emailAddress?: string }[] = permData.permissions || [];

        // Find and delete the permission matching the email
        const matching = permissions.find(
          p => p.emailAddress?.toLowerCase() === emailLower
        );
        if (matching) {
          const delResponse = await timedFetch(
            `${GOOGLE_DRIVE_API_BASE}/files/${child.id}/permissions/${matching.id}`,
            { method: 'DELETE', headers }
          );
          if (delResponse.ok) {
            revokedCount++;
          } else {
            console.warn(`revokeFolderAccess: failed to revoke from "${child.name}":`, delResponse.status);
          }
        }

        // Also recurse into child folders to catch nested shared files
        const nestedRevoked = await this.revokeFromChildren(child.id, email, accessToken, timedFetch, depth + 1);
        if (nestedRevoked) revokedCount++;
      }

      if (depth === 0) {
        console.info(`revokeFolderAccess: revoked ${revokedCount} permissions for ${email}`);
      }
      return revokedCount > 0;
    } catch (error) {
      console.error('Error revoking from children:', error);
      return false;
    }
  }

  // ============================================================================
  // ENTITY DATA STORAGE (JSON FILES IN GOOGLE DRIVE)
  // For Pro users - ALL data stored in Google Drive
  // ============================================================================

  /**
   * Data file names for each entity type
   */
  private readonly DATA_FILES = {
    profile: '_data_profile.json',
    addresses: '_data_addresses.json',
    vehicles: '_data_vehicles.json',
    insurance: '_data_insurance.json',
    healthcare: '_data_healthcare.json',
    financial: '_data_financial.json',
    portals: '_data_portals.json',
  } as const;

  /**
   * Save entity data as JSON to Google Drive
   * Stores data in: LucidDock/{ProfileName}/_data_{entityType}.json
   */
  async saveEntityData<T>(
    profileName: string,
    entityType: keyof typeof this.DATA_FILES,
    data: T
  ): Promise<boolean> {
    const accessToken = await this.getValidAccessToken();
    if (!accessToken) {
      console.error('No valid access token for saving entity data');
      return false;
    }

    try {
      // Ensure profile folder exists
      const folderPath = `LucidDock/${profileName}`;
      const folderId = await this.ensureFolderPath(folderPath);
      if (!folderId) {
        console.error('Failed to create profile folder');
        return false;
      }

      const fileName = this.DATA_FILES[entityType];
      const jsonContent = JSON.stringify(data, null, 2);
      const blob = new Blob([jsonContent], { type: 'application/json' });

      // Check if file already exists
      const existingFileId = await this.findDataFile(folderId, fileName);

      if (existingFileId) {
        // Update existing file
        return await this.updateFileContent(existingFileId, blob);
      } else {
        // Create new file
        return await this.createDataFile(folderId, fileName, blob);
      }
    } catch (error) {
      console.error(`Error saving ${entityType} data:`, error);
      return false;
    }
  }

  /**
   * Load entity data from Google Drive JSON file
   */
  async loadEntityData<T>(
    profileName: string,
    entityType: keyof typeof this.DATA_FILES
  ): Promise<T | null> {
    const accessToken = await this.getValidAccessToken();
    if (!accessToken) {
      console.error('No valid access token for loading entity data');
      return null;
    }

    try {
      const folderPath = `LucidDock/${profileName}`;
      const folderId = await this.ensureFolderPath(folderPath);
      if (!folderId) return null;

      const fileName = this.DATA_FILES[entityType];
      const fileId = await this.findDataFile(folderId, fileName);
      if (!fileId) return null;

      // Download file content
      const response = await this.driveFetch(
        `${GOOGLE_DRIVE_API_BASE}/files/${fileId}?alt=media`,
        { headers: { Authorization: `Bearer ${accessToken}` } }
      );

      if (response.ok) {
        const text = await response.text();
        return JSON.parse(text) as T;
      }

      return null;
    } catch (error) {
      console.error(`Error loading ${entityType} data:`, error);
      return null;
    }
  }

  /**
   * Find a data file in a folder by name
   */
  private async findDataFile(folderId: string, fileName: string): Promise<string | null> {
    const accessToken = await this.getValidAccessToken();
    if (!accessToken) return null;

    try {
      const response = await this.driveFetch(
        `${GOOGLE_DRIVE_API_BASE}/files?` +
          new URLSearchParams({
            q: `name='${fileName}' and '${folderId}' in parents and trashed=false`,
            fields: 'files(id)',
            spaces: 'drive',
          }),
        { headers: { Authorization: `Bearer ${accessToken}` } }
      );

      if (response.ok) {
        const data = await response.json();
        if (data.files && data.files.length > 0) {
          return data.files[0].id;
        }
      }

      return null;
    } catch (error) {
      console.error('Error finding data file:', error);
      return null;
    }
  }

  /**
   * Create a new JSON data file
   */
  private async createDataFile(folderId: string, fileName: string, blob: Blob): Promise<boolean> {
    const accessToken = await this.getValidAccessToken();
    if (!accessToken) return false;

    try {
      const metadata = {
        name: fileName,
        parents: [folderId],
        mimeType: 'application/json',
      };

      const formData = new FormData();
      formData.append('metadata', new Blob([JSON.stringify(metadata)], { type: 'application/json' }));
      formData.append('file', blob);

      const response = await this.driveFetch(
        `${GOOGLE_DRIVE_UPLOAD_API}/files?uploadType=multipart`,
        {
          method: 'POST',
          headers: { Authorization: `Bearer ${accessToken}` },
          body: formData,
        }
      );

      return response.ok;
    } catch (error) {
      console.error('Error creating data file:', error);
      return false;
    }
  }

  /**
   * Update existing file content
   */
  private async updateFileContent(fileId: string, blob: Blob): Promise<boolean> {
    const accessToken = await this.getValidAccessToken();
    if (!accessToken) return false;

    try {
      const response = await this.driveFetch(
        `${GOOGLE_DRIVE_UPLOAD_API}/files/${fileId}?uploadType=media`,
        {
          method: 'PATCH',
          headers: {
            Authorization: `Bearer ${accessToken}`,
            'Content-Type': 'application/json',
          },
          body: blob,
        }
      );

      return response.ok;
    } catch (error) {
      console.error('Error updating data file:', error);
      return false;
    }
  }

  /**
   * Delete entity data file from Google Drive
   */
  async deleteEntityData(
    profileName: string,
    entityType: keyof typeof this.DATA_FILES
  ): Promise<boolean> {
    const accessToken = await this.getValidAccessToken();
    if (!accessToken) return false;

    try {
      const folderPath = `LucidDock/${profileName}`;
      const folderId = await this.ensureFolderPath(folderPath);
      if (!folderId) return true; // Folder doesn't exist, nothing to delete

      const fileName = this.DATA_FILES[entityType];
      const fileId = await this.findDataFile(folderId, fileName);
      if (!fileId) return true; // File doesn't exist, nothing to delete

      return await this.deleteFile(fileId);
    } catch (error) {
      console.error(`Error deleting ${entityType} data:`, error);
      return false;
    }
  }

  /**
   * Load all entity data for a profile from Google Drive
   * Returns null if Google Drive is not connected or data doesn't exist
   */
  async loadAllProfileData(profileName: string): Promise<{
    addresses: unknown[] | null;
    vehicles: unknown[] | null;
    insurance: unknown[] | null;
    healthcare: unknown[] | null;
    financial: unknown[] | null;
    portals: unknown[] | null;
  } | null> {
    if (!this.isConnected()) return null;

    const [addresses, vehicles, insurance, healthcare, financial, portals] = await Promise.all([
      this.loadEntityData<unknown[]>(profileName, 'addresses'),
      this.loadEntityData<unknown[]>(profileName, 'vehicles'),
      this.loadEntityData<unknown[]>(profileName, 'insurance'),
      this.loadEntityData<unknown[]>(profileName, 'healthcare'),
      this.loadEntityData<unknown[]>(profileName, 'financial'),
      this.loadEntityData<unknown[]>(profileName, 'portals'),
    ]);

    return { addresses, vehicles, insurance, healthcare, financial, portals };
  }

  /**
   * Check if profile data exists in Google Drive
   */
  async hasProfileData(profileName: string): Promise<boolean> {
    if (!this.isConnected()) return false;

    try {
      const folderPath = `LucidDock/${profileName}`;
      const folderId = await this.ensureFolderPath(folderPath);
      if (!folderId) return false;

      // Check if any data file exists
      const fileId = await this.findDataFile(folderId, this.DATA_FILES.addresses);
      return fileId !== null;
    } catch {
      return false;
    }
  }

  // ============================================================================
  // ACCOUNT DELETION CLEANUP
  // ============================================================================

  /**
   * Delete app-generated data from Google Drive (selective cleanup).
   * Removes _data_*.json files and .luciddock config folder.
   * Keeps user-uploaded documents, photos, and folder structure.
   * Called during account deletion for "Keep my files" option.
   */
  async deleteAppDataFromDrive(): Promise<{ success: boolean; deletedCount: number; errors: string[] }> {
    const accessToken = await this.getValidAccessToken();
    if (!accessToken) return { success: false, deletedCount: 0, errors: ['No valid access token'] };

    const rootFolderId = this.getRootFolderId();
    if (!rootFolderId) return { success: false, deletedCount: 0, errors: ['Root folder not found'] };

    const errors: string[] = [];
    let deletedCount = 0;
    const dataFileNames = new Set(Object.values(this.DATA_FILES));

    try {
      const rootChildren = await this.listFilesInFolder(rootFolderId);

      for (const child of rootChildren) {
        // Preserve .luciddock config folder — it contains the encrypted profile data
        // (profile.encrypted.json). Keeping it allows returning users who sign up again
        // with the same Google account to automatically recover their old data.
        if (child.name === '.luciddock' && child.mimeType === MIME_TYPES.FOLDER) {
          continue;
        }

        // For profile subfolders, delete _data_*.json files
        if (child.mimeType === MIME_TYPES.FOLDER) {
          const folderContents = await this.listFilesInFolder(child.id);

          for (const file of folderContents) {
            if (dataFileNames.has(file.name as typeof this.DATA_FILES[keyof typeof this.DATA_FILES])) {
              const deleted = await this.deleteFile(file.id);
              if (deleted) deletedCount++;
              else errors.push(`Failed to delete ${file.name} in ${child.name}`);
            }
          }

          // If profile folder is now empty, delete it too
          const remaining = await this.listFilesInFolder(child.id);
          if (remaining.length === 0) {
            const deleted = await this.deleteFile(child.id);
            if (deleted) deletedCount++;
            else errors.push(`Failed to delete empty folder ${child.name}`);
          }
        }
      }

      // If root folder is now empty, delete it
      const rootRemaining = await this.listFilesInFolder(rootFolderId);
      if (rootRemaining.length === 0) {
        await this.deleteFile(rootFolderId);
        deletedCount++;
      }
    } catch (error) {
      errors.push(error instanceof Error ? error.message : 'Unknown error during cleanup');
    }

    // Always disconnect regardless of errors
    this.disconnect();

    return { success: errors.length === 0, deletedCount, errors };
  }

  /**
   * Delete the entire LucidDock folder from Google Drive.
   * Removes all app data AND user-uploaded files.
   * Called during account deletion for "Delete everything" option.
   */
  async deleteAllDriveData(): Promise<{ success: boolean; error?: string }> {
    const accessToken = await this.getValidAccessToken();
    if (!accessToken) return { success: false, error: 'No valid access token' };

    const rootFolderId = this.getRootFolderId();
    if (!rootFolderId) return { success: false, error: 'Root folder not found' };

    try {
      const deleted = await this.deleteFile(rootFolderId);
      this.disconnect();
      return { success: deleted, error: deleted ? undefined : 'Failed to delete LucidDock folder' };
    } catch (error) {
      this.disconnect();
      return { success: false, error: error instanceof Error ? error.message : 'Unknown error' };
    }
  }
}

// Export singleton instance
export const googleDriveService = new GoogleDriveService();
